import { ReactNode } from 'react'
import { Link as RouterLink } from 'react-router'
import {
  Box,
  ListItemButton,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material'
import { Notification } from '@/types/notifications'
import { getNotificationOpenPath } from '@/utils/notifications'

type NotificationButtonProps = {
  notification: Notification
  isRead: boolean
  onClick: () => void
  documentName?: string
  documentLoading?: boolean
  titleEnd?: ReactNode
  sx?: SxProps<Theme>
}

export default function NotificationButton({
  notification,
  isRead,
  onClick,
  documentName,
  documentLoading = false,
  titleEnd,
  sx = [],
}: NotificationButtonProps) {
  const openPath = getNotificationOpenPath(notification)
  const clickable =
    Boolean(openPath) && (documentLoading || documentName !== undefined)
  const linkProps = openPath ? { component: RouterLink, to: openPath } : {}
  const rowSx = [
    { py: 1.5, pl: 1.25, pr: 2 },
    ...(Array.isArray(sx) ? sx : [sx]),
  ]

  const content = (
    <>
      <Box sx={{ width: 16, flexShrink: 0, pt: 0.75 }}>
        {!isRead && (
          <Box
            aria-label="Unread"
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'primary.main',
            }}
          />
        )}
      </Box>
      <Stack spacing={0.25} sx={{ minWidth: 0, flexGrow: 1 }}>
        <Stack direction="row" spacing={1} justifyContent="space-between">
          <Typography variant="body2" fontWeight={500} noWrap>
            {notification.title}
          </Typography>
          {titleEnd}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {notification.body}
        </Typography>
        {documentName && (
          <Typography variant="caption" color="primary" noWrap>
            {documentName}
          </Typography>
        )}
      </Stack>
    </>
  )

  if (!clickable) {
    return (
      <Box
        sx={[
          {
            display: 'flex',
            alignItems: 'flex-start',
            width: '100%',
            boxSizing: 'border-box',
          },
          ...rowSx,
        ]}
      >
        {content}
      </Box>
    )
  }

  return (
    <ListItemButton
      {...linkProps}
      onClick={onClick}
      alignItems="flex-start"
      sx={rowSx}
    >
      {content}
    </ListItemButton>
  )
}
